import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Steps, 
    Form, 
    Input, 
    Select, 
    Button, 
    Card, 
    Typography, 
    Divider, 
    Space, 
    Radio, 
    DatePicker, 
    TimePicker, 
    InputNumber,
    message,
    Modal,
    Tag,
    Image,
    Row,
    Col
} from 'antd';
import { 
    ShoppingCartOutlined, 
    UserOutlined, 
    CalendarOutlined, 
    CreditCardOutlined,
    CheckCircleOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePayment } from '../../contexts/PaymentContext';
import PaymentMethodSelector from '../Payment/PaymentMethodSelector';
import PaymentProcessor from '../Payment/PaymentProcessor';
import api from '../../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

function Checkout() {
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();
    const { 
        cartItems, 
        getCartTotal, 
        getCartItemCount, 
        getCartItemsByVendor,
        clearCart 
    } = useCart();
    const { selectedPaymentMethod, setPaymentMethod } = usePayment();
    
    const [currentStep, setCurrentStep] = useState(0);
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [orderData, setOrderData] = useState({});
    const [cities, setCities] = useState([]);
    const [showPaymentProcessor, setShowPaymentProcessor] = useState(false);
    const [bookingData, setBookingData] = useState(null);

    useEffect(() => {
        if (cartItems.length === 0) {
            navigate('/menu');
            return;
        }
        
        fetchCities();
        
        // Pre-fill user data if authenticated
        if (isAuthenticated && user) {
            form.setFieldsValue({
                name: user.name,
                email: user.email,
                phone: user.phone,
            });
        }
    }, [cartItems, isAuthenticated, user, navigate]);

    const fetchCities = async () => {
        try {
            const response = await api.get('/cities');
            setCities(response.data.data || []);
        } catch (error) {
            console.error('Failed to fetch cities:', error);
        }
    };

    const steps = [
        {
            title: 'Order Review',
            icon: <ShoppingCartOutlined />,
            description: 'Review your items'
        },
        {
            title: 'Event Details',
            icon: <CalendarOutlined />,
            description: 'Event information'
        },
        {
            title: 'Contact Info',
            icon: <UserOutlined />,
            description: 'Your details'
        },
        {
            title: 'Payment',
            icon: <CreditCardOutlined />,
            description: 'Complete order'
        }
    ];

    const handleNext = async () => {
        try {
            if (currentStep === 1) {
                await form.validateFields(['event_date', 'event_time', 'guest_count', 'city_id']);
            } else if (currentStep === 2) {
                await form.validateFields(['name', 'email', 'phone', 'address']);
            }
            
            const values = form.getFieldsValue();
            setOrderData({ ...orderData, ...values });
            setCurrentStep(currentStep + 1);
        } catch (error) {
            console.error('Validation failed:', error);
        }
    };

    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
    };

    const handlePlaceOrder = async () => {
        try {
            setLoading(true);
            const values = form.getFieldsValue();
            const finalOrderData = { ...orderData, ...values };

            const orderPayload = {
                items: cartItems.map(item => ({
                    menu_item_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    customizations: item.customizations
                })),
                event_date: finalOrderData.event_date.format('YYYY-MM-DD'),
                event_time: finalOrderData.event_time.format('HH:mm'),
                guest_count: finalOrderData.guest_count,
                city_id: finalOrderData.city_id,
                customer_name: finalOrderData.name,
                customer_email: finalOrderData.email,
                customer_phone: finalOrderData.phone,
                address: finalOrderData.address,
                special_requests: finalOrderData.special_requests || '',
                payment_method: selectedPaymentMethod,
                total_amount: getCartTotal()
            };

            const response = await api.post('/bookings', orderPayload);
            
            if (response.data.success) {
                // Set booking data for payment processing
                setBookingData({
                    ...orderPayload,
                    booking_id: response.data.booking.id,
                    business_name: 'Spoon & Spice',
                    description: `Catering order for ${finalOrderData.guest_count} guests`,
                    currency: 'INR'
                });
                
                // Show payment processor
                setShowPaymentProcessor(true);
            }
        } catch (error) {
            console.error('Order placement failed:', error);
            message.error('Failed to place order. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePaymentSuccess = (paymentResult) => {
        try { localStorage.setItem('last_booking_id', String(paymentResult?.booking?.id || '')); } catch(_) {}
        navigate('/booking-confirmation', { 
            state: { 
                booking: paymentResult.booking,
                payment: paymentResult,
                orderData: bookingData 
            } 
        });
    };

    const handlePaymentError = (error) => {
        message.error('Payment failed. Please try again.');
        setShowPaymentProcessor(false);
    };

    const handlePaymentCancel = () => {
        setShowPaymentProcessor(false);
    };

    const renderOrderReview = () => (
        <div className="space-y-6">
            <Card title={<span className="text-xl font-bold">Order Summary</span>} className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
                <div className="space-y-4">
                    {getCartItemsByVendor().map((vendorGroup) => (
                        <div key={vendorGroup.vendor_id}>
                            <Title level={5} className="text-orange-600 mb-3 font-bold text-lg">
                                {vendorGroup.vendor_name}
                            </Title>
                            {vendorGroup.items.map((item, index) => (
                                <div key={`${item.id}-${index}`} className="flex justify-between items-start mb-4 pb-4 border-b border-orange-100 last:border-b-0 bg-gradient-to-r from-orange-50/30 to-transparent p-3 rounded-lg">
                                    <div className="flex gap-4 flex-1">
                                        <Image
                                            src={item.image}
                                            alt={item.name}
                                            width={70}
                                            height={70}
                                            className="rounded-xl object-cover shadow-md"
                                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqKg16B6oUSDAOKW1sVQK3O8CBJXJGYhhyB4BqLYoMDD1M2AwbJYxgYvh4iB0eQo8sSAxSgUo3w6B9pA4oaVfcZQBY9eJ+TAgxur+4UqgMDPtZGBgY/m//+f5sBgZ2DQ+H/9//v2f+//t/HwMDvQaGv5cA8cFj4hSXNnYAAABWZVhJZk1NACoAAAAIAAGHaQAEAAAAAQAAABoAAAAAAAOShgAHAAAAEgAAABgAAAAAAKACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAMAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxAAPwCdABmX/9k="
                                        />
                                        <div className="flex-1">
                                            <Text strong className="text-base font-bold text-gray-900">{item.name}</Text>
                                            <div className="text-sm text-gray-600 font-medium mt-1">
                                                Qty: {item.quantity} × ₹{item.price}
                                            </div>
                                            {Object.keys(item.customizations).length > 0 && (
                                                <div className="mt-2 flex flex-wrap gap-1">
                                                    {Object.entries(item.customizations).map(([key, value]) => (
                                                        <Tag key={key} size="small" className="tag-modern">
                                                            {key}: {value}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <Text strong className="price-modern text-lg font-bold">
                                        ₹{(item.price * item.quantity).toLocaleString()}
                                    </Text>
                                </div>
                            ))}
                            <Divider />
                        </div>
                    ))}
                    <div className="flex justify-between items-center text-xl font-bold pt-4 border-t-2 border-orange-200 bg-gradient-to-r from-orange-50 to-transparent p-4 rounded-lg">
                        <span className="text-gray-800">Total Amount:</span>
                        <span className="price-modern text-2xl">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                </div>
            </Card>
        </div>
    );

    const renderEventDetails = () => (
        <Card title={<span className="text-xl font-bold">Event Details</span>} className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="event_date"
                        label="Event Date"
                        rules={[{ required: true, message: 'Please select event date' }]}
                    >
                        <DatePicker 
                            className="w-full"
                            disabledDate={(current) => {
                                if (!current) return false;
                                const today = dayjs().startOf('day');
                                return current.isBefore(today);
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="event_time"
                        label="Event Time"
                        rules={[{ required: true, message: 'Please select event time' }]}
                    >
                        <TimePicker className="w-full" format="HH:mm" />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="guest_count"
                        label="Number of Guests"
                        rules={[{ required: true, message: 'Please enter guest count' }]}
                    >
                        <InputNumber min={1} max={1000} className="w-full" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="city_id"
                        label="City"
                        rules={[{ required: true, message: 'Please select city' }]}
                    >
                        <Select placeholder="Select city">
                            {cities.map(city => (
                                <Option key={city.id} value={city.id}>
                                    {city.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item name="special_requests" label="Special Requests">
                <TextArea 
                    rows={4} 
                    placeholder="Any special requirements or dietary restrictions..."
                />
            </Form.Item>
        </Card>
    );

    const renderContactInfo = () => (
        <Card title={<span className="text-xl font-bold">Contact Information</span>} className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="name"
                        label="Full Name"
                        rules={[{ required: true, message: 'Please enter your name' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[
                            { required: true, message: 'Please enter your email' },
                            { type: 'email', message: 'Please enter valid email' }
                        ]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        name="phone"
                        label="Phone Number"
                        rules={[{ required: true, message: 'Please enter your phone number' }]}
                    >
                        <Input />
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item
                name="address"
                label="Event Address"
                rules={[{ required: true, message: 'Please enter event address' }]}
            >
                <TextArea rows={3} placeholder="Complete address where the event will be held..." />
            </Form.Item>
        </Card>
    );

    const renderPayment = () => (
        <div className="space-y-6">
            <PaymentMethodSelector 
                amount={getCartTotal()}
                onPaymentMethodChange={setPaymentMethod}
                selectedMethod={selectedPaymentMethod}
            />

            <Card title={<span className="text-xl font-bold">Order Summary</span>} className="shadow-lg border border-orange-100/50 modern-food-card" style={{ borderRadius: '20px' }}>
                <div className="space-y-3">
                    <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Subtotal ({getCartItemCount()} items)</span>
                        <span className="font-semibold">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Service Charges</span>
                        <span className="font-semibold">₹0</span>
                    </div>
                    <div className="flex justify-between text-gray-700">
                        <span className="font-medium">Taxes</span>
                        <span className="font-semibold">Included</span>
                    </div>
                    <Divider className="border-orange-200" />
                    <div className="flex justify-between text-xl font-bold bg-gradient-to-r from-orange-50 to-transparent p-4 rounded-lg">
                        <span className="text-gray-800">Total Amount</span>
                        <span className="price-modern">₹{getCartTotal().toLocaleString()}</span>
                    </div>
                </div>
            </Card>
        </div>
    );

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return renderOrderReview();
            case 1:
                return renderEventDetails();
            case 2:
                return renderContactInfo();
            case 3:
                return renderPayment();
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-orange-50/30 via-white to-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4">
                <div className="mb-8">
                    <Button 
                        icon={<ArrowLeftOutlined />} 
                        onClick={() => navigate('/menu')}
                        className="mb-4 rounded-xl border-2 border-gray-200 hover:border-orange-400 hover:text-orange-600"
                    >
                        Back to Menu
                    </Button>
                    <Title level={2} className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-orange-600 to-orange-500 bg-clip-text text-transparent">Checkout</Title>
                </div>

                <div className="mb-8">
                    <Steps current={currentStep} items={steps} responsive />
                </div>

                <Form form={form} layout="vertical" requiredMark={false} scrollToFirstError>
                    {renderStepContent()}
                </Form>

                <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-0 justify-between">
                    {currentStep > 0 && (
                        <Button onClick={handlePrev}>
                            Previous
                        </Button>
                    )}
                    <div className="sm:ml-auto">
                        {currentStep < steps.length - 1 ? (
                            <Button type="primary" onClick={handleNext} className="btn-vibrant">
                                Next
                            </Button>
                        ) : (
                            <Button 
                                type="primary" 
                                size="large"
                                loading={loading}
                                onClick={handlePlaceOrder}
                                className="btn-vibrant px-8 h-12 text-base font-bold"
                            >
                                {selectedPaymentMethod === 'cod' ? 'Place Order' : 'Proceed to Payment'}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Payment Processor Modal */}
            <Modal
                title="Processing Payment"
                open={showPaymentProcessor}
                onCancel={handlePaymentCancel}
                footer={null}
                width={600}
                closable={false}
                maskClosable={false}
            >
                <PaymentProcessor
                    bookingData={bookingData}
                    selectedPaymentMethod={selectedPaymentMethod}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    onCancel={handlePaymentCancel}
                />
            </Modal>
        </div>
    );
}

export default Checkout;
